import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../services/axiosInstance';
import './HolidayCalendarButton.css';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Small calendar icon that pops open the company holiday list. Replaces the
// old embedded attendance calendar on the Dashboard - this only shows
// holidays, for every role.
const HolidayCalendarButton = () => {
  const [open, setOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const loadHolidays = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/api/holidays');
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const active = rows
        .filter((holiday) => String(holiday.status || 'active').toLowerCase() === 'active')
        .sort((a, b) => new Date(a.Date) - new Date(b.Date));
      setHolidays(active);
      setLoaded(true);
    } catch (err) {
      console.error('Unable to load holidays', err);
      setError('Holidays could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      if (next && !loaded && !loading) loadHolidays();
      return next;
    });
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="holiday-widget" ref={containerRef}>
      <button
        type="button"
        className="holiday-widget-trigger"
        onClick={toggleOpen}
        title="Company holidays"
        aria-label="Company holidays"
        aria-expanded={open}
      >
        <i className="bi bi-calendar3"></i>
      </button>

      {open && (
        <div className="holiday-widget-panel" role="dialog" aria-label="Company holidays">
          <div className="holiday-widget-head">
            <span><i className="bi bi-calendar-event"></i> Company Holidays</span>
            <button type="button" className="holiday-widget-close" onClick={() => setOpen(false)} aria-label="Close">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="holiday-widget-body">
            {loading ? (
              <p className="holiday-widget-empty">Loading holidays…</p>
            ) : error ? (
              <p className="holiday-widget-empty">{error}</p>
            ) : holidays.length ? (
              <ul className="holiday-widget-list">
                {holidays.map((holiday) => {
                  const date = new Date(holiday.Date);
                  const isPast = !Number.isNaN(date.getTime()) && date < today;
                  return (
                    <li key={holiday.id} className={isPast ? 'is-past' : ''}>
                      <span className="holiday-widget-date">{formatDate(holiday.Date)}</span>
                      <span className="holiday-widget-name">{holiday.Holiday}</span>
                      {!isPast && <span className="holiday-widget-upcoming">Upcoming</span>}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="holiday-widget-empty">No holidays published yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayCalendarButton;
